import { getAgentBySlug, getAgentColor } from '../../utils/agents';
import type { ActivityEvent } from '../../types/activity';
import { Activity } from 'lucide-react';

interface OfficeLiveActivityProps {
  events: ActivityEvent[];
  loading: boolean;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function timeAgo(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ago`;
}

function statusColor(status: string): string {
  switch (status) {
    case 'success':
      return 'text-success';
    case 'error':
      return 'text-error';
    case 'running':
      return 'text-warning';
    default:
      return 'text-text-muted';
  }
}

function statusDot(status: string): string {
  switch (status) {
    case 'success':
      return 'bg-success';
    case 'error':
      return 'bg-error';
    case 'running':
      return 'bg-warning animate-pulse-dot';
    default:
      return 'bg-text-muted';
  }
}

export default function OfficeLiveActivity({ events, loading }: OfficeLiveActivityProps) {
  const recentEvents = events.slice(0, 20);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border-subtle">
        <Activity className="w-3.5 h-3.5 text-accent-purple" />
        <span className="text-xs font-medium text-text-primary">Live Activity</span>
        {loading && (
          <span className="ml-auto w-1.5 h-1.5 rounded-full bg-accent-purple animate-pulse-dot" />
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {recentEvents.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-[10px] text-text-muted">No recent activity</p>
          </div>
        ) : (
          <div className="divide-y divide-border-subtle/50">
            {recentEvents.map((event) => {
              const agent = getAgentBySlug(event.agent);
              return (
                <div
                  key={event.id}
                  className="flex items-start gap-2 px-3 py-2 hover:bg-bg-secondary/50 transition-colors"
                >
                  {/* Agent mini avatar */}
                  {agent ? (
                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center text-[7px] font-bold text-white shrink-0 mt-0.5 ${getAgentColor(agent.department)}`}
                    >
                      {getInitials(agent.name)}
                    </div>
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-text-muted/30 shrink-0 mt-0.5" />
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${statusDot(event.status)}`} />
                      <span className={`text-[10px] font-medium truncate ${statusColor(event.status)}`}>
                        {agent?.shortName || event.agent}
                      </span>
                    </div>
                    <p className="text-[9px] text-text-muted leading-tight mt-0.5 line-clamp-2">
                      {event.summary}
                    </p>
                    <span className="text-[8px] text-text-muted/60">{timeAgo(event.timestamp)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
